import { ReactNode, useEffect, useRef } from 'react'

import { Card } from '../card/Card'
import Loading from '../loading/Loading'

import { WidgetDataResult, WidgetTone } from '../../types/widget/widget'

type WidgetProps<T> = {
  result: WidgetDataResult<T>
  children: (data: T | null) => ReactNode
  title?: string
  className?: string
  bodyClassName?: string
  tone?: WidgetTone
  onError?: (message: string) => void
  refreshTrigger?: unknown
  renderWhenEmpty?: boolean
  bare?: boolean
}

export const Widget = <T,>({
  result,
  children,
  title,
  className = '',
  bodyClassName = '',
  tone = WidgetTone.DEFAULT,
  onError,
  refreshTrigger,
  renderWhenEmpty = false,
  bare = false,
}: WidgetProps<T>) => {
  const { data, loading, error, refetch } = result
  const previousRefreshTriggerRef = useRef(refreshTrigger)
  const refetchRef = useRef(refetch)
  const bodyClass = `widget-body ${bodyClassName}`
  const displayLoading = loading

  useEffect(() => {
    refetchRef.current = refetch
  }, [refetch])

  useEffect(() => {
    if (!error) {
      return
    }

    onError?.(error)
  }, [error, onError])

  useEffect(() => {
    if (Object.is(previousRefreshTriggerRef.current, refreshTrigger)) {
      return
    }

    previousRefreshTriggerRef.current = refreshTrigger
    void refetchRef.current?.()
  }, [refreshTrigger])

  if (data == null && !displayLoading && (Boolean(error) || !renderWhenEmpty)) {
    return null
  }

  const renderWidget = (
    body: ReactNode,
    loading = false,
    showLoadingOverlay = false
  ) => {
    const content = (
      <div
        className={`widget${showLoadingOverlay ? ' is-refreshing' : ''}${bare ? ' is-bare' : ''}`}
        aria-busy={loading || undefined}
      >
        {title ? <h3 className="widget-title">{title}</h3> : null}

        <div className="widget-content">
          <div className={bodyClass}>{body}</div>
        </div>

        {showLoadingOverlay ? (
          <div className="widget-loading-overlay">
            <Loading variant="inline" />
          </div>
        ) : null}
      </div>
    )

    if (bare) {
      return <div className={className}>{content}</div>
    }

    return (
      <Card tone={tone} className={className}>
        {content}
      </Card>
    )
  }

  if (data == null && displayLoading) {
    return renderWidget(<Loading variant="inline" />, true)
  }

  return renderWidget(
    children(data),
    displayLoading,
    displayLoading && data != null
  )
}
